import { instanceScopeIndex, qualifiedKey } from '@bmo/form-core';
import dayjs from 'dayjs';

import type { WorkflowTemplate } from '@/api/workflows';

import { type FieldValue } from './FieldRenderer';

/**
 * Substitute `{field:<internalName>}` and `{today:<format>}` tokens in a
 * `StepFileEntry.filenameTemplate` string against the current answers.
 *
 * `{field:<name>}` resolves against the AcroForm names a field is MAPPED to
 * (`pdfMappings[].acroFieldName`), case-sensitive and exact — not against any
 * separate "internal name" property, which fields do not carry. That is why
 * RDSP's templates read `{field:EMP5608_E[0].Page1[0]...txtF_holder_last_name[0]}`.
 * A field mapped to several AcroForm names matches on any of them. Missing / blank
 * fields resolve to the empty string — the caller can decide whether to
 * fall back to the label. Multi-value fields (CheckboxGroup, arrays)
 * join with `_`; booleans → `yes` / `no`; dates → the raw ISO value.
 *
 * `{field:a|field:b}` is a token with FALLBACKS: its `|`-separated alternatives are tried in
 * order and the first non-blank one wins (e.g. the Account Holder's last name, else the Public
 * Department/Institution name). Each alternative names its own kind, so
 * `{field:a|today:YYYYMMDD}` works too. All blank → the empty string, like a single blank field.
 *
 * `{today:<format>}` supports the common patterns BMO PDFs use:
 *   MMDDYYYY / YYYYMMDD / DDMMYYYY / YYYY-MM-DD.
 * Any dayjs format string is accepted; unknown formats fall through to
 * dayjs's own parsing (returns whatever dayjs makes of them).
 *
 * Filesystem-hostile characters (`\/:*?"<>|` and control chars) are
 * sanitized to `_` after substitution so a stray value can't produce a
 * broken filename. A `.pdf` extension is appended when absent so the
 * caller doesn't have to remember. Falls back to `${label}.pdf` when
 * the template is null / empty / resolves to only whitespace.
 */
export function renderFilename(
  template: string | null | undefined,
  fallbackLabel: string,
  values: Record<string, FieldValue>,
  workflowTemplate: WorkflowTemplate,
  /** Extension to append when the resolved name doesn't already carry
   *  one. Defaults to `pdf` — the PDF-generation path — but callers
   *  packaging advisor-uploaded assets can pass `jpg`, `png`, `zip`,
   *  etc. so the original file type is preserved. Leading dots are
   *  tolerated (`.pdf` and `pdf` behave the same). */
  extension: string = 'pdf',
  /** The entry's `instanceScope` — which instance of a repeatable section its `{field:…}` tokens
   *  read, as a 0-based decimal string. Null / absent / malformed = instance 0, the behaviour
   *  every caller had before this existed. A bad scope must not blank a filename, so this
   *  degrades rather than throwing; the server refuses a non-index at authoring time instead. */
  instanceScope: string | null | undefined = null,
): string {
  const ext = extension.replace(/^\./, '').trim() || 'pdf';
  const fallback = ensureExt(sanitize(fallbackLabel) || 'document', ext);
  if (!template || !template.trim()) return fallback;

  // Build the internalName → candidate-value-keys lookup. A workflow
  // can legitimately carry the SAME internalName in multiple sections —
  // branch A ("Beneficiary IS Account Holder") and branch B ("Beneficiary
  // is NOT AH, Individual") both mint an AH1 Last Name field, both
  // stamped `lastname1`. Only ONE of those sections is visible at a
  // time, so on lookup we walk all candidates and return the first key
  // that actually holds a value. Repeatable sections contribute
  // `inst:<sectionId>:0:<fieldId>`; non-repeatable contribute the bare
  // field id. Layout blocks and unnamed fields are skipped.
  const valueKeysByInternalName = new Map<string, string[]>();
  // Which repeatable instance this entry's tokens read. 0 unless the entry says otherwise:
  // a document can BE one instance's document ("Transfer Forms – Accountholder 2"), and
  // before this it could only ever be named after the FIRST instance — so an AH2 form gated
  // on "there are 2 holders" came out carrying AH1's last name.
  const instanceIdx = instanceScopeIndex(instanceScope) ?? 0;
  for (const step of workflowTemplate.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (const section of step.sections) {
      const isRepeatable = section.repeatable === true;
      for (const row of section.rows) {
        for (const col of row.columns) {
          const f = col.field;
          if (!f) continue;
          const key = isRepeatable ? qualifiedKey(section.id, instanceIdx, f.id) : f.id;
          // A field can now map to several AcroForm names (across PDFs); the
          // `{field:<name>}` token matches on any of them. Dedupe names within
          // one field so a field mapped to the same name twice doesn't push a
          // duplicate value key.
          const names = new Set<string>();
          for (const m of f.pdfMappings ?? []) {
            const name = m.acroFieldName?.trim();
            if (name) names.add(name);
          }
          for (const name of names) {
            const bucket = valueKeysByInternalName.get(name);
            if (bucket) bucket.push(key);
            else valueKeysByInternalName.set(name, [key]);
          }
        }
      }
    }
  }
  const resolveFieldValue = (internalName: string): string => {
    const candidates = valueKeysByInternalName.get(internalName);
    if (!candidates) return '';
    for (const key of candidates) {
      const raw = values[key];
      const formatted = formatFieldValue(raw);
      if (formatted.length > 0) return formatted;
    }
    return '';
  };

  /** One `kind:argument` alternative, or null when the kind is not a known token. */
  const resolveAlternative = (kind: string, argument: string): string | null => {
    // An answer's own spaces become '_' ("ACME Holdings Inc" → "ACME_Holdings_Inc", QA Reg/Non-Reg
    // D-10): a filename is passed around by systems that split on them. The template's literal
    // text is the author's and is left alone.
    if (kind === 'field') return resolveFieldValue(argument).trim().replace(/\s+/g, '_');
    if (kind === 'today') return dayjs().format(argument);
    return null;
  };

  const substituted = template.replace(/\{([a-zA-Z]+):([^}]*)\}/g, (match, kind, arg) => {
    let resolved = '';
    for (const alt of parseAlternatives(String(kind), String(arg))) {
      const value = resolveAlternative(alt.kind, alt.argument);
      // Unknown token — leave the whole literal in place so a typo is visible
      // rather than silently producing an empty slot.
      if (value === null) return match;
      resolved = value;
      if (value.trim().length > 0) break;
    }
    return resolved;
  });

  const cleaned = sanitize(substituted).trim();
  if (!cleaned) return fallback;
  return ensureExt(cleaned, ext);
}

/**
 * Split a token's body into its `|`-separated alternatives: `{field:a|field:b}` →
 * `[field:a, field:b]`. The first alternative's kind is the token's own; each later one names
 * its own kind (`field:b`, `today:YYYYMMDD`).
 *
 * When any later part lacks a `kind:` prefix the pipe is not an alternative separator, and the
 * whole body is ONE argument — exactly how the token resolved before alternatives existed, so a
 * dayjs format that happens to contain `|` keeps meaning what it meant.
 */
function parseAlternatives(kind: string, arg: string): Array<{ kind: string; argument: string }> {
  const [first, ...rest] = arg.split('|');
  const alternatives = [{ kind, argument: first ?? '' }];
  for (const part of rest) {
    const m = /^([a-zA-Z]+):(.*)$/.exec(part);
    if (!m) return [{ kind, argument: arg }];
    alternatives.push({ kind: m[1]!, argument: m[2]! });
  }
  return alternatives;
}

/** Coerce any FieldValue to a filename-friendly string chunk. */
function formatFieldValue(v: FieldValue): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (Array.isArray(v)) {
    return v
      .map((item) => (typeof item === 'string' || typeof item === 'number' ? String(item) : ''))
      .filter(Boolean)
      .join('_');
  }
  return '';
}

/** Strip characters that break filenames on Windows / macOS / Linux. */
function sanitize(s: string): string {
  return (
    s
      .replace(/[\\/:*?"<>|]/g, '_')
      // Collapse control chars (\x00-\x1F and DEL) that would either
      // display as odd glyphs or get outright rejected by macOS Finder.
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1F\x7F]/g, '_')
  );
}

/** Append the given extension when the name doesn't already end in it
 *  (case-insensitive). Empty / `pdf`-equivalent extensions all work. */
function ensureExt(name: string, extension: string): string {
  const re = new RegExp(`\\.${escapeRegExp(extension)}$`, 'i');
  return re.test(name) ? name : `${name}.${extension}`;
}

/** Escape user-supplied text for use inside a RegExp — the extension is
 *  originator-authored and could contain a `.`. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
